<template>
  <NotificationRequest />
  <div :class="['app', layoutClass]">
    <AppHead>
      <title>ProofOfID | Secure Identity Verification and Sharing</title>
      <meta
        content="ProofOfID offers the best digital identity verification and sharing platform to protect, control, and share identities securely."
        name="description"
      >
      <meta
        content="ProofOfID | Secure Identity Verification and Sharing"
        property="og:title"
      >
      <meta
        content="ProofOfID offers the best digital identity verification and sharing platform to protect, control, and share identities securely."
        property="og:description"
      >
      <link
        href="/favicon-small.png"
        rel="shortcut icon"
        type="image/x-icon"
      >
      <link
        href="/favicon-large.png"
        rel="apple-touch-icon"
      >
      <link
        rel="icon"
        href="/favicon-small.png"
      >
    </AppHead>
    <the-navbar
      v-if="showNavigation"
      @sidenav-toggle="toggleSideNav"
    />
    <div class="main-content">
      <SideNav
        v-if="showNavigation"
        :is-sidenav-open="isSideNavOpen"
      />
      <div
        v-show="isDesktop || !isSideNavOpen"
        class="content"
      >
        <router-view
          v-show="showPage"
          :key="`${$route.path}${JSON.stringify($route.query)}`"
          @ready="onPageReady"
          @not-ready="loadPage"
        />
        <AppSpinner v-show="!showPage" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import NotificationRequest from '@/components/NotificationRequest.vue'
import NProgress from 'nprogress'
import TheNavbar from '@/components/TheNavbar'
import SideNav from '@/components/navigation/side/SideNav'

export default {
  name: 'App',
  components: { NotificationRequest, TheNavbar, SideNav },
  data () {
    return {
      showPage: false,
      showRequestModal: false,
      currentRequest: {
        requesterName: 'Requester Name',
        identifiers: ['Identifier 1', 'Identifier 2'],
        requestedAt: new Date(),
        isActive: true,
        isApproved: false
      },
      timeoutId: null,
      isSideNavOpen: false,
      isDesktop: window.innerWidth > 965
    }
  },
  computed: {
    ...mapGetters('auth', ['getConfirmedUser']),
    data () {
      // Access the requests from the store's state
      return this.$store.state.requests
    },
    layoutClass () {
      return this.$route.meta.layoutClass || ''
    },
    showNavigation () {
      // Hide navbar/sidebar on guest pages (login, MFA, etc.) even if user is authenticated
      // This prevents navbar from appearing during MFA confirmation before navigation
      return this.getConfirmedUser && !this.$route.meta.requiresGuest && !this.$route.meta.hideNavigation
    }
  },
  async created () {
    NProgress.configure({
      speed: 200,
      showSpinner: false
    })
    this.$router.beforeEach(() => {
      this.showPage = false
      this.isSideNavOpen = false
      NProgress.start()
    })
  },
  mounted () {
    // Only set up session timeout if user is authenticated
    if (this.getConfirmedUser) {
      this.setupSessionTimeout()
    }
    window.addEventListener('resize', this.updateScreen)
  },
  beforeUnmount () {
    // Cleanup event listeners and timeout
    clearTimeout(this.timeoutId)
    window.removeEventListener('resize', this.updateScreen)
    document.removeEventListener('mousemove', this.resetTimeout)
    document.removeEventListener('click', this.resetTimeout)
    document.removeEventListener('keypress', this.resetTimeout)
    document.removeEventListener('scroll', this.resetTimeout)
  },
  methods: {
    onPageReady () {
      this.showPage = true
      NProgress.done()
    },
    loadPage () {
      this.showPage = false
      NProgress.start()
    },
    setupSessionTimeout () {
      // Set initial timeout
      this.resetTimeout()

      // Add event listeners to reset the timeout on user interaction
      document.addEventListener('mousemove', this.resetTimeout)
      document.addEventListener('click', this.resetTimeout)
      document.addEventListener('keypress', this.resetTimeout)
      document.addEventListener('scroll', this.resetTimeout)
    },
    resetTimeout () {
      // Clear existing timeout
      clearTimeout(this.timeoutId)

      // Only set timeout if user is authenticated
      if (!this.getConfirmedUser) {
        return
      }

      // Set a max session of 5 mins (default) or from environment
      const timeoutDuration = process.env.VUE_APP_SESSION_TIMEOUT ? parseInt(process.env.VUE_APP_SESSION_TIMEOUT) : 300000

      // Set a new timeout
      this.timeoutId = setTimeout(() => {
        this.handleSessionTimeout()
      }, timeoutDuration)
    },
    async handleSessionTimeout () {
      // Show a warning modal instead of immediately logging out
      if (this.$store.getters['auth/getConfirmedUser']) {
        const shouldLogout = confirm('Your session is about to expire. Click OK to stay logged in or Cancel to logout.')

        if (shouldLogout) {
          // Reset the timeout for another period
          this.resetTimeout()
        } else {
          // Perform proper logout
          await this.$store.dispatch('auth/signOut')
        }
      }
    },
    toggleSideNav (isOpen) {
      this.isSideNavOpen = isOpen
    },
    updateScreen () {
      this.isDesktop = window.innerWidth > 965
    }
  }
}
</script>

<style>
@import "assets/style.css";
@import "assets/modals.css";
@import "~nprogress/nprogress.css";
#nprogress .bar {
  background: #57ad8d !important;
}

.main-content {
  display: flex;
  flex: 1;
  position: relative;
  min-height: 100vh;
  width: 100vw;
  overflow-x: hidden;
  overflow-y: auto;
}

.content {
  overflow: auto;
  flex: 1;
  padding: 20px;
}

.side-nav {
  width: 250px; /* Adjust this value based on the width of your SideNav */
}

.main-content:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://cdn.prod.website-files.com/65f8f1d0dc04cb6fb982697d/668d77b036fa3b551b413242_dot3.png');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  opacity: 0.3; /* Make the image a bit transparent */
  z-index: 1;
}

.main-content::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end)) no-repeat;
  opacity: 0.01; /* Make the image a bit transparent */
  z-index: 2;
}

.main-content > * {
  position: relative;
  z-index: 3;
}
</style>
